import React, { useMemo, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Box,
  Card,
  Typography,
  TextField,
  MenuItem,
  Skeleton,
  Autocomplete,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import DeleteIcon from "@mui/icons-material/Delete";
import CancelIcon from "@mui/icons-material/Cancel";
import { useEmpyreanContext } from "../../../context/EmpyreanContext";
import MuiConfirmDialog from "../../common/MuiConfirmDialog/MuiConfirmDialog";
import useStyles from "./EmpyreanOPDB.styles";
import { useUserContext } from "../../../context/UserContext";

const booleanOptions = [
  { label: "True", value: true },
  { label: "False", value: false },
];

const renderBooleanText = (value) => (value ? "True" : "False");

const EmpyreanOPDB = () => {
  const { classes } = useStyles();
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [selectedRowIndex, setSelectedRowIndex] = useState(null);
  const [selectedLabelName, setSelectedLabelName] = useState("");

  const {
    rows,
    empyreanListLoading,
    addRow,
    updateRow,
    removeRow,
    saveNewRow,
    updateExistingRow,
    getModelDatabaseOptions,
  } = useEmpyreanContext();

  const { selectedUserDetailLoading, selectedUserDetail } = useUserContext();

  const { regions } = selectedUserDetail || {};

  const allsfcs = useMemo(() => {
    return (
      regions
        ?.flatMap((region) => region?.sfcs?.map((sfc) => sfc) || [])
        .sort((a, b) => a.businessEntityID.localeCompare(b.businessEntityID)) ||
      []
    );
  }, [regions]);

  const businessEntityOptions = useMemo(() => {
    return [
      ...new Set(allsfcs.map((sfc) => sfc.businessEntityID).filter(Boolean)),
    ];
  }, [allsfcs]);

  const handleBusinessEntityChange = (index, value) => {
    updateRow(index, {
      businessEntityID: value,
      modelDatabase: "",
      monthEnd: "",
    });
  };

  const handleEditClick = (index) => {
    const row = rows[index];
    updateRow(index, {
      isEditing: true,
      originalRow: {
        businessEntityID: row.businessEntityID,
        modelDatabase: row.modelDatabase,
        monthEnd: row.monthEnd,
        advancedNII: row.advancedNII,
        eveMethodEmpyrean: row.eveMethodEmpyrean,
        treasuryKRIGlobalView: row.treasuryKRIGlobalView,
      },
    });
  };

  const handleCancelEdit = (index) => {
    const row = rows[index];

    if (row.originalRow) {
      updateRow(index, {
        ...row.originalRow,
        isEditing: false,
        originalRow: null,
      });
    } else {
      updateRow(index, {
        isEditing: false,
      });
    }
  };

  const handleSaveClick = async (index) => {
    const row = rows[index];

    try {
      if (row.isPersisted) {
        await updateExistingRow(index);
      } else {
        await saveNewRow(index);
      }
    } catch (error) {
      alert(error.message);
    }
  };

  const handleDeleteClick = (index) => {
    setSelectedRowIndex(index);
    setSelectedLabelName(
      rows[index]?.modelDatabase || rows[index]?.businessEntityID || "N/A",
    );
    setShowConfirmDialog(true);
  };

  const handleConfirmDeleteRow = async () => {
    if (selectedRowIndex === null) return;

    try {
      await removeRow(selectedRowIndex);
    } catch (error) {
      alert(error.message);
    } finally {
      setShowConfirmDialog(false);
      setSelectedRowIndex(null);
      setSelectedLabelName("");
    }
  };

  const handleCancelDeleteRow = () => {
    setShowConfirmDialog(false);
    setSelectedRowIndex(null);
    setSelectedLabelName("");
  };

  const isViewMode = (row) => row.isPersisted && !row.isEditing;
  const isEditingExistingRow = (row) => row.isPersisted && row.isEditing;
  const isNewRow = (row) => !row.isPersisted;

  const isRowSaveEnabled = (row) => {
    return (
      !!row.businessEntityID &&
      !!row.modelDatabase &&
      !!row.monthEnd &&
      row.advancedNII !== null &&
      row.advancedNII !== undefined &&
      row.eveMethodEmpyrean !== null &&
      row.eveMethodEmpyrean !== undefined &&
      row.treasuryKRIGlobalView !== null &&
      row.treasuryKRIGlobalView !== undefined
    );
  };

  return (
    <div className={classes.page}>
      <Card sx={{ p: 2 }}>
        <Typography
          sx={{
            color: "#6a6a6a",
            margin: 2,
            fontWeight: "bold",
          }}
        >
          The Empyran OP DB management resolves the various models to link with
          respective Business Entity ID that application uses.
        </Typography>

        <Table>
          <TableHead>
            <TableRow>
              <TableCell>BusinessEntityID</TableCell>
              <TableCell>ModelDatabase</TableCell>
              <TableCell>MonthEnd</TableCell>
              <TableCell>AdvancedNII</TableCell>
              <TableCell>EVEMethodEmpyrean</TableCell>
              <TableCell>TreasuryKRIGlobalView</TableCell>
              <TableCell>Update</TableCell>
              <TableCell>Delete</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {empyreanListLoading || selectedUserDetailLoading ? (
              Array.from({ length: 5 }).map((_, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <Skeleton variant="text" width="80%" />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text" width="90%" />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text" width="70%" />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text" width="60%" />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text" width="60%" />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text" width="60%" />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="circular" width={24} height={24} />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="circular" width={24} height={24} />
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <>
                {rows.map((row, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      {isViewMode(row) ? (
                        <Typography>{row.businessEntityID || "-"}</Typography>
                      ) : (
                        <Autocomplete
                          size="small"
                          fullWidth
                          options={businessEntityOptions}
                          value={
                            businessEntityOptions.includes(row.businessEntityID)
                              ? row.businessEntityID
                              : null
                          }
                          onChange={(event, newValue) => {
                            handleBusinessEntityChange(index, newValue || "");
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label=""
                              placeholder="Search business entity"
                            />
                          )}
                          disabled={isEditingExistingRow(row)}
                          isOptionEqualToValue={(option, value) =>
                            option === value
                          }
                        />
                      )}
                    </TableCell>

                    <TableCell>
                      {isViewMode(row) ? (
                        <Typography>{row.modelDatabase || "-"}</Typography>
                      ) : (
                        <Autocomplete
                          size="small"
                          fullWidth
                          options={getModelDatabaseOptions()}
                          getOptionLabel={(option) =>
                            option.modelDatabase || ""
                          }
                          value={
                            getModelDatabaseOptions().find(
                              (option) =>
                                option.modelDatabase === row.modelDatabase,
                            ) || null
                          }
                          onChange={(event, newValue) => {
                            if (!newValue) {
                              updateRow(index, {
                                modelDatabase: "",
                                monthEnd: "",
                              });
                              return;
                            }

                            updateRow(index, {
                              modelDatabase: newValue.modelDatabase || "",
                              monthEnd: newValue.cpDate || "",
                            });
                          }}
                          isOptionEqualToValue={(option, value) =>
                            option.modelDatabase === value.modelDatabase
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder="Search model database"
                              variant="outlined"
                            />
                          )}
                          filterOptions={(options, state) =>
                            options.filter((option) =>
                              option.modelDatabase
                                .toLowerCase()
                                .includes(state.inputValue.toLowerCase()),
                            )
                          }
                          disabled={isEditingExistingRow(row)}
                        />
                      )}
                    </TableCell>

                    <TableCell>
                      {isViewMode(row) ? (
                        <Typography>{row.monthEnd || "-"}</Typography>
                      ) : (
                        <>
                          {row.monthEnd ? (
                            <TextField
                              size="small"
                              fullWidth
                              value={row.monthEnd}
                              disabled
                            />
                          ) : (
                            <Typography color="error">
                              CP Date not exists
                            </Typography>
                          )}
                        </>
                      )}
                    </TableCell>

                    <TableCell>
                      {isViewMode(row) ? (
                        <Typography>
                          {renderBooleanText(row.advancedNII)}
                        </Typography>
                      ) : (
                        <TextField
                          select
                          size="small"
                          fullWidth
                          value={String(row.advancedNII)}
                          onChange={(e) =>
                            updateRow(index, {
                              advancedNII: e.target.value === "true",
                            })
                          }
                          disabled={isViewMode(row)}
                        >
                          {booleanOptions.map((option) => (
                            <MenuItem
                              key={option.value}
                              value={String(option.value)}
                            >
                              {option.label}
                            </MenuItem>
                          ))}
                        </TextField>
                      )}
                    </TableCell>

                    <TableCell>
                      {isViewMode(row) ? (
                        <Typography>
                          {renderBooleanText(row.eveMethodEmpyrean)}
                        </Typography>
                      ) : (
                        <TextField
                          select
                          size="small"
                          fullWidth
                          value={String(row.eveMethodEmpyrean)}
                          onChange={(e) =>
                            updateRow(index, {
                              eveMethodEmpyrean: e.target.value === "true",
                            })
                          }
                          disabled={isViewMode(row)}
                        >
                          {booleanOptions.map((option) => (
                            <MenuItem
                              key={option.value}
                              value={String(option.value)}
                            >
                              {option.label}
                            </MenuItem>
                          ))}
                        </TextField>
                      )}
                    </TableCell>

                    <TableCell>
                      {isViewMode(row) ? (
                        <Typography>
                          {renderBooleanText(row.treasuryKRIGlobalView)}
                        </Typography>
                      ) : (
                        <TextField
                          select
                          size="small"
                          fullWidth
                          value={String(row.treasuryKRIGlobalView)}
                          onChange={(e) =>
                            updateRow(index, {
                              treasuryKRIGlobalView: e.target.value === "true",
                            })
                          }
                          disabled={isViewMode(row)}
                        >
                          {booleanOptions.map((option) => (
                            <MenuItem
                              key={option.value}
                              value={String(option.value)}
                            >
                              {option.label}
                            </MenuItem>
                          ))}
                        </TextField>
                      )}
                    </TableCell>

                    <TableCell>
                      {isNewRow(row) ? (
                        <IconButton
                          onClick={() => handleSaveClick(index)}
                          disabled={!isRowSaveEnabled(row)}
                        >
                          <SaveIcon />
                        </IconButton>
                      ) : row.isEditing ? (
                        <>
                          <IconButton
                            onClick={() => handleSaveClick(index)}
                            disabled={!isRowSaveEnabled(row)}
                          >
                            <SaveIcon />
                          </IconButton>
                          <IconButton onClick={() => handleCancelEdit(index)}>
                            <CancelIcon />
                          </IconButton>
                        </>
                      ) : (
                        <IconButton onClick={() => handleEditClick(index)}>
                          <EditIcon />
                        </IconButton>
                      )}
                    </TableCell>

                    <TableCell>
                      <IconButton
                        onClick={() => handleDeleteClick(index)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </>
            )}
          </TableBody>
        </Table>

        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={addRow}
            disabled={selectedUserDetailLoading || empyreanListLoading}
          >
            Add Row
          </Button>
        </Box>
      </Card>

      <MuiConfirmDialog
        open={showConfirmDialog}
        title="Confirm Deletion"
        message={`Are you sure you want to permanently delete "${selectedLabelName}"?`}
        onConfirm={handleConfirmDeleteRow}
        onCancel={handleCancelDeleteRow}
      />
    </div>
  );
};

export default EmpyreanOPDB;
